import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';
import { PlacesOfService, type PlaceOfService } from '@census/shared';

interface RoomFormState {
  displayName: string;
  capacity: number;
  placeOfService: PlaceOfService;
  notes: string;
}

const EMPTY: RoomFormState = {
  displayName: '',
  capacity: 1,
  placeOfService: 'OFFICE',
  notes: '',
};

export function RoomForm({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: any | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RoomFormState>(EMPTY);

  useEffect(() => {
    if (editing) {
      setForm({
        displayName: editing.displayName ?? '',
        capacity: editing.capacity ?? 1,
        placeOfService: editing.placeOfService ?? 'OFFICE',
        notes: editing.notes ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const save = useMutation({
    mutationFn: () => {
      const body: any = {
        type: 'ROOM',
        displayName: form.displayName,
        capacity: form.capacity,
        placeOfService: form.placeOfService,
        notes: form.notes || undefined,
        serviceTypes: [],
        defaultDays: [],
        staffResourceIds: [],
      };
      if (editing) {
        const { type, defaultDays, staffResourceIds, serviceTypes, ...patch } = body;
        return api.patch(`/resources/${editing.id}`, patch).then((r) => r.data);
      }
      return api.post('/resources', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      qc.invalidateQueries({ queryKey: ['resources-active'] });
      toast.success(editing ? 'Room updated' : 'Room created');
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit room' : 'New room'}
      subtitle="Rooms / stations bookable by appointment. Capacity = how many concurrent bookings allowed."
      size="md"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!form.displayName || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create room'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Display name" required>
          <input
            className="input"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder="e.g. Therapy Room 2"
          />
        </FormField>
        <FormField label="Capacity" hint="How many concurrent bookings the room admits.">
          <input
            type="number"
            className="input"
            min={1}
            max={50}
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
          />
        </FormField>
        <FormField label="Place of service" required>
          <select
            className="input"
            value={form.placeOfService}
            onChange={(e) => setForm({ ...form, placeOfService: e.target.value as PlaceOfService })}
          >
            {PlacesOfService.map((p) => (
              <option key={p} value={p}>
                {p === 'HOME_COMMUNITY' ? 'Community' : p}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Notes">
          <input
            className="input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Has UDS station + sink"
          />
        </FormField>
      </div>
    </Modal>
  );
}
